import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const QuickScan = () => {
  const [domain, setDomain] = useState('');
  const [status, setStatus] = useState('idle'); // idle, scanning, completed, failed, rate_limited
  const [results, setResults] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');
  const [taskId, setTaskId] = useState(null);
  const resultsEndRef = useRef(null);

  const startScan = async (e) => {
    e.preventDefault();
    if (!domain) return;
    
    setStatus('scanning');
    setResults([]);
    setErrorMsg('');
    setTaskId(null);

    try {
      // POST to our new Quick Scan endpoint
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/quick-scan`, { domain });
      setTaskId(response.data.task_id);
    } catch (err) {
      if (err.response && err.response.status === 429) {
        setStatus('rate_limited');
        setErrorMsg('Rate limit exceeded (3 scans per hour). Please try again later.');
      } else {
        setStatus('failed');
        setErrorMsg(err.response?.data?.detail || 'Failed to start scan.');
      }
    }
  };

  useEffect(() => {
    let eventSource;
    if (taskId && status === 'scanning') {
      eventSource = new EventSource(`${process.env.REACT_APP_API_URL}/quick-scan/stream/${taskId}`);

      eventSource.onmessage = (e) => {
        const data = JSON.parse(e.data);
        
        if (data.status === 'connected') {
          setResults((prev) => [...prev, '> Connecting to scanner... [OK]']);
        } else if (data.status === 'running') {
          setResults((prev) => [...prev, `> Initializing scan for target: ${domain}...`]);
        } else if (data.status === 'finding') {
          // Format finding
          const findingMsg = `[!] Found variant: ${data.data.domain} (Risk: ${data.data.risk_level})`;
          setResults((prev) => [...prev, findingMsg]);
        } else if (data.status === 'completed') {
          setResults((prev) => [...prev, `> Scan completed. Found ${data.findings_count} resolving variants.`]);
          setStatus('completed');
          eventSource.close();
        } else if (data.status === 'failed') {
          setResults((prev) => [...prev, `> Scan failed: ${data.error}`]);
          setStatus('failed');
          eventSource.close();
        }
      };

      eventSource.onerror = (err) => {
        console.error("EventSource error:", err);
        eventSource.close();
        setStatus('failed');
        setErrorMsg('Connection to scanner lost.');
      };
    }

    return () => {
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [taskId, status, domain]);

  // Auto-scroll to bottom of results
  useEffect(() => {
    if (resultsEndRef.current) {
      resultsEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [results]);

  return (
    <div className="max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 mt-12 mb-0">
      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-glow overflow-hidden border border-gray-200 dark:border-dark-600">
        <div className="p-6 sm:p-8">
          <h3 className="text-xl leading-6 font-semibold text-gray-900 dark:text-white mb-4 text-left">
            Try a Quick Scan
          </h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 mb-6 text-left">
            Enter your domain to run a fast, limited-scope typosquatting scan. See live results as we find them.
          </p>
          
          <form onSubmit={startScan} className="mt-5 sm:flex sm:items-center">
            <div className="w-full sm:max-w-xs relative">
              <input
                type="text"
                name="domain"
                id="domain"
                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-dark-600 rounded-md bg-white dark:bg-dark-900 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 py-3 px-4"
                placeholder="example.com"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                disabled={status === 'scanning'}
              />
            </div>
            <button
              type="submit"
              disabled={status === 'scanning' || !domain}
              className="mt-3 w-full inline-flex items-center justify-center px-4 py-3 border border-transparent shadow-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {status === 'scanning' ? 'Scanning...' : 'Scan Now'}
            </button>
          </form>

          {/* Error messages */}
          {errorMsg && (
            <div className="mt-4 p-3 bg-red-900 border border-red-700 text-red-100 rounded text-sm">
              {errorMsg}
            </div>
          )}

          {/* Terminal-like Results Container */}
          {(results.length > 0 || status === 'scanning') && (
            <div className="mt-8 bg-black rounded-md p-4 font-mono text-sm overflow-hidden h-64 flex flex-col relative border border-gray-700 shadow-inner">
              <div className="flex items-center space-x-2 mb-3 border-b border-gray-800 pb-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-gray-500 text-xs ml-2">quick_scan_terminal</span>
              </div>
              
              <div className="flex-1 overflow-y-auto space-y-1 scrollbar-thin scrollbar-thumb-gray-700">
                {results.map((line, idx) => (
                  <div 
                    key={idx} 
                    className={`${
                      line.includes('[!]') ? 'text-yellow-400' : 
                      line.includes('[OK]') ? 'text-green-400' : 
                      line.includes('failed') ? 'text-red-400' : 
                      'text-gray-300'
                    } animate-pulse-once`}
                  >
                    {line}
                  </div>
                ))}
                {status === 'scanning' && (
                  <div className="text-gray-500 animate-pulse">_</div>
                )}
                <div ref={resultsEndRef} />
              </div>
            </div>
          )}

          {/* Upsell Message */}
          <p className="mt-6 text-sm text-gray-500 dark:text-gray-400 text-center">
            This is a limited quick scan. <Link to="/login" className="text-primary-600 dark:text-primary-400 hover:text-primary-500 underline font-medium">Log in</Link> for comprehensive detection including DNS anomalies, dynamic combosquatting, bitsquatting, and 1000+ TLDs.
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuickScan;
